import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import './AdminUserDetail.css';

function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});
  const [overrideValue, setOverrideValue] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [actionLog, setActionLog] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);

  const fetchActionLog = useCallback(async () => {
    setLoadingActions(true);
    try {
      const response = await adminAPI.getUserActions(userId);
      setActionLog(response.data);
    } catch (err) {
      console.error('Error fetching action log:', err);
    } finally {
      setLoadingActions(false);
    }
  }, [userId]);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await adminAPI.getUserDetails(userId);
      setUser(response.data);
      setOverrideValue(response.data.aiCoachUsageOverride != null ? String(response.data.aiCoachUsageOverride) : '');
      setFormData({
        email: response.data.email || '',
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || '',
        subscriptionPlan: response.data.subscriptionPlan || 'free',
        subscriptionStatus: response.data.subscriptionStatus || 'active',
        stripeCustomerId: response.data.stripeCustomerId || '',
        stripeSubscriptionId: response.data.stripeSubscriptionId || '',
        isAdmin: response.data.isAdmin || false,
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      alert('Failed to load user details');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchUserDetails();
    fetchActionLog();
  }, [fetchUserDetails, fetchActionLog]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    if (!window.confirm("Update this user's information?")) {
      return;
    }

    setUpdating(true);
    try {
      await adminAPI.updateUser(userId, formData);
      alert('User updated successfully!');
      fetchUserDetails(); // Refresh data
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    const confirmText = `DELETE ${user.email}`;
    const userInput = prompt(
      `⚠️ PERMANENTLY DELETE USER\n\nThis will delete:\n• User account\n• All teams\n• All graphics\n• All roster data\n\nType "${confirmText}" to confirm:`
    );

    if (userInput !== confirmText) {
      alert('Deletion cancelled - text did not match');
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      alert('User deleted successfully');
      navigate('/admin');
    } catch (error) {
      console.error('Delete error:', error);
      alert(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleClearStripeData = () => {
    if (window.confirm('Clear all Stripe data for this user?')) {
      setFormData((prev) => ({
        ...prev,
        stripeCustomerId: '',
        stripeSubscriptionId: '',
        subscriptionPlan: 'free',
        subscriptionStatus: 'active',
      }));
    }
  };

  const handleRestoreAccount = async () => {
    if (!window.confirm('Restore this account? The user will be able to log in again and their deletion will be cancelled.')) return;
    try {
      await adminAPI.restoreAccount(userId);
      alert('Account restored successfully!');
      fetchUserDetails();
    } catch (error) {
      console.error('Restore account error:', error);
      alert('Failed to restore account');
    }
  };

  const handleRestoreTeam = async (teamId) => {
    if (!window.confirm('Restore this team?')) return;

    try {
      await adminAPI.restoreTeam(userId, teamId);
      alert('Team restored successfully!');
      fetchUserDetails();
    } catch (error) {
      console.error('Restore team error:', error);
      alert('Failed to restore team');
    }
  };

  const handleRestoreGraphic = async (graphicId) => {
    if (!window.confirm('Restore this graphic?')) return;

    try {
      await adminAPI.restoreGraphic(userId, graphicId);
      alert('Graphic restored successfully!');
      fetchUserDetails();
    } catch (error) {
      console.error('Restore graphic error:', error);
      alert('Failed to restore graphic');
    }
  };

  const handlePermanentDeleteTeam = async (teamId) => {
    if (
      !window.confirm('⚠️ PERMANENTLY DELETE this team? This cannot be undone!')
    )
      return;

    try {
      await adminAPI.permanentlyDeleteTeam(userId, teamId);
      alert('Team permanently deleted');
      fetchUserDetails();
    } catch (error) {
      console.error('Permanent delete team error:', error);
      alert('Failed to permanently delete team');
    }
  };

  const handlePermanentDeleteGraphic = async (graphicId) => {
    if (
      !window.confirm(
        '⚠️ PERMANENTLY DELETE this graphic? This cannot be undone!'
      )
    )
      return;

    try {
      await adminAPI.permanentlyDeleteGraphic(userId, graphicId);
      alert('Graphic permanently deleted');
      fetchUserDetails();
    } catch (error) {
      console.error('Permanent delete graphic error:', error);
      alert('Failed to permanently delete graphic');
    }
  };

  const handleRestorePlayer = async (playerId) => {
    if (!window.confirm('Restore this player?')) return;
    try {
      await adminAPI.restorePlayer(userId, playerId);
      alert('Player restored successfully!');
      fetchUserDetails();
    } catch (error) {
      console.error('Restore player error:', error);
      alert('Failed to restore player');
    }
  };

  const handlePermanentDeletePlayer = async (playerId) => {
    if (
      !window.confirm(
        '⚠️ PERMANENTLY DELETE this player and all their game history? This cannot be undone!'
      )
    )
      return;
    try {
      await adminAPI.permanentlyDeletePlayer(userId, playerId);
      alert('Player permanently deleted');
      fetchUserDetails();
    } catch (error) {
      console.error('Permanent delete player error:', error);
      alert('Failed to permanently delete player');
    }
  };

  const handleSaveOverride = async () => {
    if (!window.confirm(
      overrideValue === ''
        ? 'Clear the message override and restore tier-based limits?'
        : `Set message limit to ${overrideValue} messages/month for this user?`
    )) return;
    setSavingOverride(true);
    try {
      await adminAPI.setMessageOverride(
        userId,
        overrideValue === '' ? null : parseInt(overrideValue),
        overrideReason
      );
      setOverrideReason('');
      await fetchUserDetails();
      await fetchActionLog();
      alert('Message limit override saved.');
    } catch (err) {
      console.error('Override error:', err);
      alert('Failed to save override.');
    } finally {
      setSavingOverride(false);
    }
  };

  const getDaysDeleted = (deletedAt) => {
    const now = new Date();
    const deleted = new Date(deletedAt);
    const days = Math.floor((now - deleted) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="admin-detail-container">
          <div className="loading-state">Loading user details...</div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Navbar />
        <div className="admin-detail-container">
          <div className="empty-state">User not found</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="admin-detail-container">
        <div className="admin-detail-content">
          <button className="back-button" onClick={() => navigate('/admin')}>
            ← Back to Admin Dashboard
          </button>

          {user?.deletedAt && (
            <div style={{
              background: '#fff5f5', border: '1.5px solid #c0001a', borderRadius: 12,
              padding: '16px 20px', marginBottom: 20,
            }}>
              <div style={{ color: '#c0001a', fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
                ⚠️ Account Pending Deletion
              </div>
              <div style={{ fontSize: 14, color: '#1a1e2e', marginBottom: 12 }}>
                Deleted on: {new Date(user.deletedAt).toLocaleDateString()}<br />
                Permanent deletion scheduled: {new Date(user.deletionScheduledFor).toLocaleDateString()}
              </div>
              <button
                onClick={handleRestoreAccount}
                style={{
                  background: '#1a1e2e', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 18px', fontSize: 14,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                Restore Account
              </button>
            </div>
          )}

          {/* EDIT USER FORM */}
          <div className="detail-card">
            <div className="card-header">
              <h2>Edit User: {user.email}</h2>
              <button className="btn-delete-user" onClick={handleDeleteUser}>
                🗑️ Delete User
              </button>
            </div>

            <form onSubmit={handleUpdateUser}>
              {/* Basic Info */}
              <div className="form-section">
                <h3>Basic Information</h3>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isAdmin"
                      checked={formData.isAdmin}
                      onChange={handleChange}
                    />{' '}
                    Admin Access
                  </label>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="form-section">
                <h3>Subscription Details</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Subscription Plan</label>
                    <select
                      name="subscriptionPlan"
                      value={formData.subscriptionPlan}
                      onChange={handleChange}
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="premium">Premium</option>
                      <option value="organization">Organization</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Subscription Status</label>
                    <select
                      name="subscriptionStatus"
                      value={formData.subscriptionStatus}
                      onChange={handleChange}
                    >
                      <option value="active">Active</option>
                      <option value="canceled">Canceled</option>
                      <option value="past_due">Past Due</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Stripe Data */}
              <div className="form-section">
                <div className="section-header-with-action">
                  <h3>Stripe Integration</h3>
                  <button
                    type="button"
                    className="btn-clear-stripe"
                    onClick={handleClearStripeData}
                  >
                    Clear Stripe Data
                  </button>
                </div>

                <div className="form-group">
                  <label>Stripe Customer ID</label>
                  <input
                    type="text"
                    name="stripeCustomerId"
                    value={formData.stripeCustomerId}
                    onChange={handleChange}
                    placeholder="cus_..."
                  />
                </div>

                <div className="form-group">
                  <label>Stripe Subscription ID</label>
                  <input
                    type="text"
                    name="stripeSubscriptionId"
                    value={formData.stripeSubscriptionId}
                    onChange={handleChange}
                    placeholder="sub_..."
                  />
                </div>
              </div>

              {/* Account Metadata */}
              <div className="form-section">
                <h3>Account Metadata</h3>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <span className="metadata-label">User ID:</span>
                    <span className="metadata-value">{user.id}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Created:</span>
                    <span className="metadata-value">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Teams:</span>
                    <span className="metadata-value">
                      {user.teams?.length || 0}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Graphics:</span>
                    <span className="metadata-value">
                      {user.graphics?.length || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate('/admin')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                >
                  {updating ? 'Updating...' : '💾 Save Changes'}
                </button>
              </div>
            </form>
          </div>

          {/* DELETED TEAMS */}
          {user.deletedTeams && user.deletedTeams.length > 0 && (
            <div className="detail-card deleted-items-card">
              <h2>🗑️ Deleted Teams (Recoverable)</h2>
              <p className="admin-note">
                Teams are kept for 15 days after deletion. After that, they're
                permanently removed.
              </p>

              <div className="deleted-items-list">
                {user.deletedTeams.map((team) => {
                  const daysDeleted = getDaysDeleted(team.deletedAt);
                  const daysRemaining = 15 - daysDeleted;

                  return (
                    <div key={team.id} className="deleted-item-admin">
                      <div className="deleted-item-info">
                        <div className="deleted-item-name">{team.name}</div>
                        <div className="deleted-item-meta">
                          {team.ageGroup} • {team.season}
                        </div>
                        <span
                          className={`deleted-age ${
                            daysRemaining > 0 ? 'restorable' : 'expired'
                          }`}
                        >
                          {daysRemaining > 0
                            ? `${daysRemaining} days left to restore`
                            : 'Expired - will be auto-deleted'}
                        </span>
                      </div>

                      <div className="deleted-item-actions">
                        <button
                          className="btn-restore-admin"
                          onClick={() => handleRestoreTeam(team.id)}
                        >
                          ↩️ Restore
                        </button>
                        <button
                          className="btn-permanent-delete"
                          onClick={() => handlePermanentDeleteTeam(team.id)}
                        >
                          🗑️ Delete Forever
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* DELETED PLAYERS */}
          {user.deletedPlayers && user.deletedPlayers.length > 0 && (
            <div className="detail-card deleted-items-card">
              <h2>🗑️ Deleted Players (Recoverable)</h2>
              <p className="admin-note">
                Players are kept for 15 days after deletion. After that,
                they're permanently removed.
              </p>
              <div className="deleted-items-list">
                {user.deletedPlayers.map((player) => {
                  const daysDeleted = getDaysDeleted(player.deletedAt);
                  const daysRemaining = 15 - daysDeleted;
                  return (
                    <div key={player.id} className="deleted-item-admin">
                      <div className="deleted-item-info">
                        <div className="deleted-item-name">
                          {player.firstName} {player.lastName}
                        </div>
                        <div className="deleted-item-meta">
                          {player.sport}{player.playingLevel ? ` · ${player.playingLevel}` : ''}
                        </div>
                        <span
                          className={`deleted-age ${
                            daysRemaining > 0 ? 'restorable' : 'expired'
                          }`}
                        >
                          {daysRemaining > 0
                            ? `${daysRemaining} days left to restore`
                            : 'Expired - will be auto-deleted'}
                        </span>
                      </div>
                      <div className="deleted-item-actions">
                        <button
                          className="btn-restore-admin"
                          onClick={() => handleRestorePlayer(player.id)}
                        >
                          ↩️ Restore
                        </button>
                        <button
                          className="btn-permanent-delete"
                          onClick={() => handlePermanentDeletePlayer(player.id)}
                        >
                          🗑️ Delete Forever
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* DELETED GRAPHICS */}
          {user.deletedGraphics && user.deletedGraphics.length > 0 && (
            <div className="detail-card deleted-items-card">
              <h2>🗑️ Deleted Graphics (Recoverable)</h2>
              <p className="admin-note">
                Graphics are kept for 15 days after deletion. After that,
                they're permanently removed.
              </p>

              <div className="deleted-items-list">
                {user.deletedGraphics.map((graphic) => {
                  const daysDeleted = getDaysDeleted(graphic.deletedAt);
                  const daysRemaining = 15 - daysDeleted;

                  return (
                    <div key={graphic.id} className="deleted-item-admin">
                      <div className="deleted-item-info">
                        <div className="deleted-item-name">
                          {graphic.templateType
                            ?.replace(/_/g, ' ')
                            .toUpperCase()}
                        </div>
                        <div className="deleted-item-meta">
                          Team: {graphic.Team?.name || 'Unknown'}
                        </div>
                        <span
                          className={`deleted-age ${
                            daysRemaining > 0 ? 'restorable' : 'expired'
                          }`}
                        >
                          {daysRemaining > 0
                            ? `${daysRemaining} days left to restore`
                            : 'Expired - will be auto-deleted'}
                        </span>
                      </div>

                      <div className="deleted-item-actions">
                        <button
                          className="btn-restore-admin"
                          onClick={() => handleRestoreGraphic(graphic.id)}
                        >
                          ↩️ Restore
                        </button>
                        <button
                          className="btn-permanent-delete"
                          onClick={() =>
                            handlePermanentDeleteGraphic(graphic.id)
                          }
                        >
                          🗑️ Delete Forever
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* MESSAGE LIMIT OVERRIDE */}
          <div className="detail-card">
            <div className="card-header">
              <h2>🎛️ Ask the Analyst Override</h2>
            </div>
            <div style={{ padding: '0 0 16px' }}>
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
                Current limit: <strong>
                  {user.aiCoachUsageOverride != null
                    ? `${user.aiCoachUsageOverride} messages/mo (override active)`
                    : user.subscriptionPlan === 'premium' || user.subscriptionPlan === 'organization'
                      ? '200 messages/mo (tier default)'
                      : user.subscriptionPlan === 'pro'
                        ? '20 messages/mo (tier default)'
                        : 'Blocked (free tier)'}
                </strong>
              </p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Override Limit (messages/mo)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Leave blank to use tier default"
                    value={overrideValue}
                    onChange={(e) => setOverrideValue(e.target.value)}
                    style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 14, width: 220 }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Reason (logged)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Beta tester, special request"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 14, width: '100%' }}
                  />
                </div>
                <button
                  onClick={handleSaveOverride}
                  disabled={savingOverride}
                  style={{
                    background: '#1a1e2e', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '9px 20px', fontSize: 14,
                    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {savingOverride ? 'Saving...' : 'Save Override'}
                </button>
                {overrideValue !== '' && (
                  <button
                    onClick={() => { setOverrideValue(''); }}
                    style={{
                      background: '#f1f5f9', color: '#64748b', border: '1.5px solid #e2e8f0',
                      borderRadius: 8, padding: '9px 16px', fontSize: 14,
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Clear Override
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ADMIN ACTION LOG */}
          <div className="detail-card">
            <div className="card-header">
              <h2>📋 Admin Action Log</h2>
            </div>
            {loadingActions ? (
              <div style={{ padding: '20px 0', color: '#64748b', fontSize: 14 }}>Loading log...</div>
            ) : actionLog.length === 0 ? (
              <div style={{ padding: '20px 0', color: '#94a3b8', fontSize: 14 }}>No admin actions recorded for this user.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', borderBottom: '2px solid #f1f5f9' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', borderBottom: '2px solid #f1f5f9' }}>Action</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', borderBottom: '2px solid #f1f5f9' }}>Details</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', borderBottom: '2px solid #f1f5f9' }}>By</th>
                  </tr>
                </thead>
                <tbody>
                  {actionLog.map((action) => (
                    <tr key={action.id}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f8fafc', color: '#64748b' }}>
                        {new Date(action.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f8fafc' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                          fontSize: 11, fontWeight: 700, background: '#f1f5f9', color: '#475569',
                        }}>
                          {action.actionType.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f8fafc', color: '#1e293b' }}>
                        {action.details?.reason && <span style={{ color: '#64748b' }}>{action.details.reason} — </span>}
                        {action.actionType === 'message_override' && (
                          <span>
                            {action.details?.oldOverride ?? 'default'} → {action.details?.newOverride ?? 'default'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f8fafc', color: '#64748b' }}>
                        {action.admin?.firstName} {action.admin?.lastName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminUserDetail;
